<template>
  <div>
    <q-tabs
      v-if="isDispatchDashboard"
      v-model="workloadTab"
      position="is-centered"
      class="block"
    >
      <q-tab
        name="all"
        :label="t('dvm.dashboard.all')"
      />
      <q-tab
        v-for="tenant in dispatchedTenants"
        :key="`tab_${tenant}`"
        :name="tenant"
        :label="tenant"
      />
    </q-tabs>
    <div class="workload">
      <div :class="`workload-grid-${workloadPerValuer.maxBD}`">
        <p class="q-mr-md text-bold">
          {{ t('monolith.dashboard.specialist') }}
        </p>
        <p
          v-for="BD in workloadPerValuer.maxBD + 1"
          :key="`workload_header_${BD - 1}bd`"
          class="q-mx-sm text-center text-bold"
        >
          D{{ BD - 1 }}{{ BD - 1 == 4 ? '+' : '' }}
        </p>
        <p class="q-mx-sm text-center text-bold">
          {{ t('monolith.dashboard.total') }}
        </p>
      </div>
      <div
        v-for="specialist in Object.keys(workloadPerValuer.wl)"
        :key="`workload_${specialist}`"
        :class="`workload-grid-${
          workloadPerValuer.maxBD
        } ${specialist.toLowerCase() === 'unassigned' && 'first-row'}`"
      >
        <p class="q-mr-lg text-right">
          {{ specialist.toLowerCase() === 'unassigned' ? t('monolith.dashboard.unassigned') : specialist }}
        </p>
        <p
          v-for="BD in workloadPerValuer.maxBD + 1"
          :key="`workload_${specialist}_${BD - 1}bd`"
          class="q-mx-sm text-center"
          :class="{
            'text-bold text-primary': `D${BD - 1}` in workloadPerValuer.wl[specialist],
          }"
        >
          {{ workloadPerValuer.wl[specialist][`D${BD - 1}`] || '-' }}
        </p>
        <p
          class="q-mx-sm text-center"
          :class="{
            'text-bold text-negative': workloadPerValuer.wl[specialist].total > 0,
          }"
        >
          {{ workloadPerValuer.wl[specialist].total }}
        </p>
      </div>
      <div
        v-for="(tenant, index) in Object.keys(workloadPerValuer.totalPerTenant)"
        :key="`workload_${tenant}`"
        :class="`workload-grid-${workloadPerValuer.maxBD} ${!index && 'first-tenant-row'}`"
      >
        <p class="q-mr-sm text-right">
          {{ tenant }}
        </p>
        <p
          v-for="BD in workloadPerValuer.maxBD + 1"
          :key="`workload_${tenant}_${BD - 1}bd`"
          class="q-mr-sm text-center"
          :class="{
            'text-bold text-primary': `D${BD - 1}` in workloadPerValuer.totalPerTenant[tenant],
          }"
        >
          {{ workloadPerValuer.totalPerTenant[tenant][`D${BD - 1}`] || '-' }}
        </p>
        <p
          class="q-mx-sm text-center"
          :class="{
            'text-bold text-negative': workloadPerValuer.totalPerTenant[tenant].total > 0,
          }"
        >
          {{ workloadPerValuer.totalPerTenant[tenant].total }}
        </p>
      </div>
      <div :class="`workload-grid-${workloadPerValuer.maxBD} last-row`">
        <p class="q-mr-lg text-bold text-right">
          {{ t('monolith.dashboard.total') }}
        </p>
        <p
          v-for="BD in workloadPerValuer.maxBD + 1"
          :key="`workload_totals_${BD - 1}bd`"
          class="q-mx-sm text-center"
          :class="{
            'text-bold': workloadPerValuer.totalPerBD[`D${BD - 1}`],
          }"
        >
          {{ workloadPerValuer.totalPerBD[`D${BD - 1}`] || '-' }}
        </p>
        <p class="q-mx-xm text-center text-bold text-negative">
          {{ workloadPerValuer.totalSubmitted }}
        </p>
      </div>
      <div class="text-center re-block-mt">
        <re-button
          color="main"
          :loading="valuerListQuery.isBusy"
          :label="seeFullList ? t('monolith.dashboard.hideFullList') : t('monolith.dashboard.showFullList')"
          @click="toggleFullList"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useConfig } from '@/composables/useConfig'
import useUserList from '@/composables/useUserList'
import { useWorkload } from '@/composables/useWorkload'
import enums from '@/plugins/enums.js'

const config = useConfig()
const { t } = useI18n()

const seeFullList = ref(false)
const workloadTab = ref('all')
const valuerListQuery = useUserList({ roles: 'valuer', modules: enums.modules.DVM })
const { data: slaRequests } = useWorkload()

const valuers = computed(() => {
  return valuerListQuery.data || []
})

const isDispatchDashboard = computed(() => {
  return !!config.DISPATCHED_DEPLOYMENTS?.length
})

const dispatchedTenants = computed(() => {
  if (!isDispatchDashboard.value || !slaRequests.value) return []
  const returnArray: string[] = []

  slaRequests.value.forEach((request) => {
    const tenant = request.customer
    if (tenant && !returnArray.includes(tenant)) {
      returnArray.push(tenant)
    }
  })

  return returnArray
})

const workloadPerValuer = computed(() => {
  const returnObject: {
    wl: Record<string, Record<string, number>>
    maxBD: number
    totalPerBD: Record<string, number>
    totalSubmitted: number
    totalPerTenant: Record<string, Record<string, number>>
  } = {
    wl: {},
    maxBD: 0,
    totalPerBD: {},
    totalSubmitted: 0,
    totalPerTenant: {},
  }

  if (!slaRequests.value) return returnObject

  if (seeFullList.value) {
    valuers.value.forEach((valuer) => {
      if (valuer.username) {
        returnObject.wl[valuer.username] = {
          total: 0,
        }
      }
    })
  }

  slaRequests.value.forEach((request) => {
    const tenant = isDispatchDashboard.value ? request.customer : null

    // Filter by selected tab if needed
    if (tenant && workloadTab.value !== 'all' && tenant !== workloadTab.value) {
      return
    }

    const assignee = request.valuer ? request.valuer.username : 'unassigned'

    if (!(assignee in returnObject.wl)) {
      returnObject.wl[assignee] = {
        total: 0,
      }
    }

    // Use the pre-calculated sla_days (capped at 4)
    const slaDays = Math.min(request.sla_days, 4)

    returnObject.maxBD = Math.max(slaDays, returnObject.maxBD)

    if (`D${slaDays}` in returnObject.wl[assignee]) {
      returnObject.wl[assignee][`D${slaDays}`]++
    } else {
      returnObject.wl[assignee][`D${slaDays}`] = 1
    }

    if (`D${slaDays}` in returnObject.totalPerBD) {
      returnObject.totalPerBD[`D${slaDays}`]++
    } else {
      returnObject.totalPerBD[`D${slaDays}`] = 1
    }

    returnObject.wl[assignee].total++
    returnObject.totalSubmitted++

    if (isDispatchDashboard.value && workloadTab.value === 'all' && tenant) {
      if (!(tenant in returnObject.totalPerTenant)) {
        returnObject.totalPerTenant[tenant] = { total: 0 }
      }

      if (`D${slaDays}` in returnObject.totalPerTenant[tenant]) {
        returnObject.totalPerTenant[tenant][`D${slaDays}`]++
      } else {
        returnObject.totalPerTenant[tenant][`D${slaDays}`] = 1
      }

      returnObject.totalPerTenant[tenant].total++
    }
  })

  return returnObject
})

const toggleFullList = () => {
  seeFullList.value = !seeFullList.value
}
</script>
<style lang="scss" scoped>
@mixin gridColumnsGenerator($maxAmount, $baseClass) {
  @for $i from 0 to $maxAmount {
    .#{$baseClass}-#{$i} {
      display: grid;
      grid-template-columns: auto repeat($i + 1, 3rem) 5rem;
      // grid-template-columns: 25rem repeat($i + 1, 1fr);

      order: 2;

      &.first-tenant-row {
        border-top: 1px solid $border-light;
        align-self: stretch;
      }

      &.last-row {
        order: 998;
        border-top: 1px solid $border-light;
        align-self: stretch;
      }

      &.first-row {
        order: 1;
      }
    }
  }
}

.workload {
  @include gridColumnsGenerator(6, 'workload-grid');

  [class^='workload-grid-'] {
    &:not(:first-child):hover {
      background: rgba(var(--q-primary), 0.1);
      width: 100%;
    }

    &:first-child {
      align-self: stretch;
      border-bottom: 1px solid $border-light;
      order: 0;
    }
  }

  width: max-content;
  margin-left: auto;
  margin-right: auto;

  button {
    order: 999;
  }
}
</style>
